import { PRODUCT_LECTURA } from 'root/lib/constants';
import { getTimestamp } from 'root/lib/DatetimeManager';
import { getBucketData, formatPlacement } from 'lib/utilityFunctions';
import { logRumError, logRumAction } from 'root/lib/DDRum';
import gameIds from '../data/GameIds';
import { persistStateTypes } from '../../reducers/persistStateReducer';
import workshopState, { workshopStateForDemo, workshopStateForLecturaDemo } from './state/workshopState';
import { getShouldRePlacementModalBeShown } from './helper';
import {
  clearFailureCountersForTargetGameLevels,
  masterAllLevelsOfTargetSkillId,
  getQuestKeyFromQuestId,
  calculateBenchmark,
  calculateBookTrack,
  initWorkshopGames,
  initWorkshopEreader,
  updateEreaderProgress,
  updateTFEProgress,
  updateVocabProgress,
  updateActivityStatus,
  getWorkshopHistoryFromBucket,
  fixPlaceholderGames,
  fixCorruptedHardcodedGames,
  fixCorruptedWorkshopStep,
  fixMissingRewardAtCuriosoStep,
} from '../lib/workshopUtility';
import {
  REWARD_TYPES,
  GRADES,
  VOCAB_PROGRESS,
  SEASON_PART,
  SEASON,
  WORKSHOP_STATE_VERSION,
  WORKSHOP_BENCHMARK_IDS,
  WORKSHOP_BOOK_TRACK_IDS,
} from '../data/constants';
import { updateWorkshopGames } from './adaptivity/contentServiceAdaptivity';

const workshopReducer = (state = workshopState, { type, gameId, payload, ...action }) => {
  if (type === persistStateTypes.SET_AUTH) {
    const { placementPeriod, placementGrade } = payload;
    const placement = formatPlacement(placementPeriod, placementGrade);
    const benchmark = calculateBenchmark(placementPeriod, placementGrade);
    const bookTrack = calculateBookTrack(placementPeriod, placementGrade);
    if (payload.season === SEASON.GRADE_4_5) {
      logRumAction('Workshop: init book track', {
        bookTrack,
        benchmark,
      });
    }
    return {
      ...state,
      grade: payload.grade || GRADES.GRADE_4, // defaults to grade 4
      seasonPart: payload.seasonPart || SEASON_PART.A,
      currentQuestId: payload.seasonPart === SEASON_PART.B ? 309 : 301,
      placement,
      benchmark,
      bookTrack,
      workshop: initWorkshopEreader(initWorkshopGames(state.workshop, benchmark), bookTrack),
    };
  }
  if (gameId !== gameIds.METAGAME) {
    return state;
  }

  switch (type) {
    case persistStateTypes.REQUESTRESPONSE_GAMESTATE_ALL: {
      let localState = { ...state };

      const { demoSeason } = ((action.stateData[gameIds.TEACHER_DASHBOARD_SETTINGS] || {}).GameData || {});
      const isDemoMode = [SEASON.GRADE_K_1, SEASON.GRADE_2_3, SEASON.GRADE_4_5].includes(demoSeason);
      if (isDemoMode) {
        // override state with hardcoded Demo state
        localState = { ...state, ...workshopStateForDemo };
        if (action.product === PRODUCT_LECTURA) {
          localState = { ...state, ...workshopStateForLecturaDemo };
        }
      }

      const {
        version,
        currentQuest,
        placement: savedPlacement,
        isChallengeQuest,
        benchmark: savedBenchmark,
        bookTrack: savedBookTrack,
        hasSeenReplacementModal = false,
        ...workshopBucketData
      } = getBucketData(action.stateData[gameIds.METAGAME_WORKSHOP]);

      if (version !== WORKSHOP_STATE_VERSION || isDemoMode) {
        return localState;
      }

      // Fix benchmark mismatch: If saved benchmark differs from local benchmark (set by SET_AUTH),
      // reinitialize workshop structure with the correct saved benchmark before merging bucket data.
      // This preserves the student's learning pathway (e.g., staying on WELL_BELOW even if placement improved).
      if (savedBenchmark && localState.benchmark !== savedBenchmark) {
        logRumAction('Workshop benchmark mismatch - preserved saved benchmark', {
          calculatedBenchmark: localState.benchmark,
          savedBenchmark,
          currentQuestId: workshopBucketData.currentQuestId,
          placement: localState.placement,
          savedPlacement,
        });
        localState = {
          ...localState,
          workshop: initWorkshopGames(localState.workshop, savedBenchmark),
          benchmark: savedBenchmark,
        };
      }

      // The book track is pinned for the school year, like the benchmark above: the saved track always
      // wins over the one calculated from the current placement, so a re-placed student's books don't flip.
      // A bucket with a benchmark but no book track was written before the WBB book split shipped, or by a
      // path that dropped the field, so treat the student as already in Season 3: they keep the normal books
      // on every quest - not just the ones already saved - and never see a mid-season switch. benchmark is
      // the signal because every workshop save writes it and it does not depend on quest or step state.
      const isPreReleaseBucket = !savedBookTrack && !!savedBenchmark;
      const bookTrack = isPreReleaseBucket ? WORKSHOP_BOOK_TRACK_IDS.NORMAL : savedBookTrack || localState.bookTrack;
      if (localState.bookTrack !== bookTrack) {
        logRumAction('Workshop: preserve book track', {
          isPreReleaseBucket,
          bookTrack,
          calculatedBookTrack: localState.bookTrack,
          savedBookTrack,
          calculatedBenchmark: localState.benchmark,
          savedBenchmark,
          calculatedPlacement: localState.placement,
          savedPlacement,
          currentQuestId: workshopBucketData.currentQuestId,
        });
        localState = {
          ...localState,
          workshop: initWorkshopEreader(localState.workshop, bookTrack),
          bookTrack,
        };
      }

      // Validate currentQuest structure to unblock students with corrupted TFE data.
      // Root cause fixed, but patch needed for already-affected students. Skip validation for challenge quests.
      const isValidCurrentQuest = (
        typeof currentQuest.TFE.episode === 'object' && currentQuest.TFE.episode !== null
      ) || isChallengeQuest;
      const localQuest = { ...localState.workshop[getQuestKeyFromQuestId(workshopBucketData.currentQuestId)] };
      let questToBeUsed = isValidCurrentQuest ? currentQuest : localQuest;

      // Fix corrupted games: placeholders and incorrect skillId→levels mappings
      if (!isChallengeQuest && questToBeUsed.games) {
        questToBeUsed = fixPlaceholderGames(questToBeUsed, currentQuest.questKey, savedBenchmark);
        questToBeUsed = fixCorruptedHardcodedGames(questToBeUsed, currentQuest.questKey, savedBenchmark);
      }

      const workshopHistory = getWorkshopHistoryFromBucket(action.stateData);

      const { placementData: { placedMethod } } = action;

      let updatedState = {
        ...localState,
        ...workshopBucketData,
        isChallengeQuest,
        workshop: {
          ...localState.workshop,
          ...workshopHistory,
          [currentQuest.questKey]: questToBeUsed,
        },
        benchmark: savedBenchmark,
        bookTrack,
        hasSeenReplacementModal,
        shouldShowReplacementModal: !hasSeenReplacementModal && getShouldRePlacementModalBeShown(
          localState.placement,
          savedPlacement,
          placedMethod,
        ),
      };

      const ereaderBookData = {};
      Object.keys(action.stateData).filter(game => game.includes('ereader')).forEach((book) => {
        ereaderBookData[book] = getBucketData(action.stateData[book]);
      });
      const tfeBucketData = getBucketData(action.stateData[gameIds.CLOSE_READING]);
      const vocabBucketData = getBucketData(action.stateData[gameIds.VOCAB]);

      if (!isChallengeQuest) {
        updatedState = updateTFEProgress(updatedState, tfeBucketData);
        updatedState = updateEreaderProgress(updatedState, ereaderBookData);
        updatedState = updateVocabProgress(updatedState, vocabBucketData);

        // Fix corrupted state were step 0 has all content complete
        // Skip fix for demo mode and end-of-everything state (challenge quest already filtered above)
        const areAllQuestsComplete = Object.values(updatedState.areAllQuestsAndChallengesComplete || {}).some(v => v);
        if (!isDemoMode && !areAllQuestsComplete) {
          const correctedStep = fixCorruptedWorkshopStep(
            updatedState.workshopStep,
            questToBeUsed,
            workshopBucketData.currentQuestId,
            updatedState.benchmark,
          );
          if (correctedStep !== updatedState.workshopStep) {
            logRumError('Workshop corruption: Step reset to 0 with complete content', {
              questKey: currentQuest.questKey,
              questId: workshopBucketData.currentQuestId,
              benchmark: updatedState.benchmark,
              wrongStep: updatedState.workshopStep,
              correctedStep,
              isOnboarding: [301, 309].includes(workshopBucketData.currentQuestId),
            });
            updatedState = {
              ...updatedState,
              workshopStep: correctedStep,
            };
          }

          // Curioso step without the TFE reward can't open the orb (AQUA-42760): rewind so it can be collected
          const rewoundStep = fixMissingRewardAtCuriosoStep(
            updatedState.workshopStep,
            updatedState.rewards?.TFE,
            workshopBucketData.currentQuestId,
            updatedState.benchmark,
          );
          if (rewoundStep !== updatedState.workshopStep) {
            logRumError('Workshop corruption: curioso step without TFE reward, rewound to reward step', {
              questId: workshopBucketData.currentQuestId,
              benchmark: updatedState.benchmark,
              wrongStep: updatedState.workshopStep,
              correctedStep: rewoundStep,
            });
            updatedState = {
              ...updatedState,
              workshopStep: rewoundStep,
            };
          }
        }
      }

      return updatedState;
    }

    case 'LOAD_WORKSHOP_GAMES_FROM_ADAPTIVITY':
      return updateWorkshopGames(state, action.contentSets);

    case 'SET_QUEST_GAME_LEVEL_MASTERED': {
      const { questId, selectedGame, level } = payload;
      const questKey = getQuestKeyFromQuestId(questId);
      const { games } = state.workshop[questKey];

      const levelsMastered = [...new Set([...selectedGame.levelsMastered, level])];

      const updatedGames = games.map((game) =>
        (game.skillId === selectedGame.skillId ? { ...game, levelsMastered } : game),
      );

      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            games: updatedGames,
          },
        },
        gameFailures: {
          ...state.gameFailures,
          [`${selectedGame.game}_${level}`]: 0,
          currentLevel: level,
        },
      };
    }

    case 'COMPLETE_STUCK_GAME': {
      const {
        questId,
        selectedGame: {
          game: gameName,
          skillId,
          levels,
        },
      } = payload;
      const questKey = getQuestKeyFromQuestId(questId);
      const { games } = state.workshop[questKey];

      const updatedGames = masterAllLevelsOfTargetSkillId(games, skillId);

      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            games: updatedGames,
          },
        },
        gameFailures: clearFailureCountersForTargetGameLevels(state.gameFailures, gameName, levels),
      };
    }

    case 'AUTO_COMPLETE_TFE_SESSION': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      const quest = { ...state.workshop[questKey] };

      quest.TFE.episode = {
        ...quest.TFE.episode,
        progress: state.benchmark === WORKSHOP_BENCHMARK_IDS.NORMAL ? 'complete' : quest.TFE.episode.progress,
        parts: quest.TFE.episode.parts.map((part, index) => ({
          ...part,
          progress: payload.index === index ? 'complete' : part.progress,
        })),
      };

      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: { ...quest },
        },
      };
    }

    case 'COMPLETE_EREADER_SESSION':
    case 'AUTO_COMPLETE_EREADER_SESSION': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      const quest = state.workshop[questKey];
      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...quest,
            eReader: {
              ...quest.eReader,
              progress: 'complete',
            },
          },
        },
      };
    }

    case 'AUTO_COMPLETE_VOCAB_SESSION': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      const quest = state.workshop[questKey];
      const sessions = [...state.workshop[questKey].vocab.sessions];
      sessions.forEach((session, index) => {
        if (session.id === payload.sessionId) {
          sessions[index].progress = VOCAB_PROGRESS.COMPLETE;
        }
      });
      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...quest,
            vocab: {
              ...quest.vocab,
              sessions: [...sessions],
            },
          },
        },
      };
    }

    case 'SET_IS_VOCAB_APP_AVAILABLE':
      return {
        ...state,
        isVocabAppAvailable: payload,
      };

    case 'SET_TRANSMITTER_STATUS':
      return updateActivityStatus(state, 'vocab', payload);

    case 'SET_TFE_STATUS':
      return updateActivityStatus(state, 'TFE', payload);

    case 'SET_BATTERY_STATUS':
      return updateActivityStatus(state, 'battery', payload);

    case 'SET_EREADER_STATUS':
      return updateActivityStatus(state, 'eReader', payload);

    case 'SET_TRANSMISSION_SEQUENCE_ID': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            transmissionSequenceId: payload.transmissionSequenceId,
            transmissionText: null,
          },
        },
      };
    }

    case 'SET_TRANSMISSION_TEXT': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            transmissionText: payload.text,
          },
        },
      };
    }

    case 'CLEAR_TRANSMISSION_SEQUENCE': {
      const questKey = getQuestKeyFromQuestId(payload.questId);
      return {
        ...state,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            transmissionText: null,
            transmissionSequenceId: null,
          },
        },
      };
    }

    case 'SELECT_CURIOSO':
      return {
        ...state,
        selectedCurioso: payload.curioso,
      };

    case 'UPDATE_CHALLENGE_QUEST_GAMES':
      return {
        ...state,
        shouldStartNewChallengeQuest: false,
        workshop: {
          ...state.workshop,
          challengeQuest: {
            ...state.workshop.challengeQuest,
            games: [
              ...payload,
            ],
          },
        },
      };

    case 'ADD_TO_REWARDS': {
      const isBookReward = payload.type === REWARD_TYPES.BOOK;
      const activityKey = isBookReward ? 'eReader' : 'TFE';
      return {
        ...state,
        rewards: {
          ...state.rewards,
          [activityKey]: isBookReward
            ? payload.name
            : [...new Set([...state.rewards.TFE, payload.name])],
        },
      };
    }

    case 'INIT_QUEST': {
      // When all quests are completed, the "next" quest doesn't exist, so we use questId = 0.
      // In this case, there is no need to set a startDate, so we just leave workshop state as it is
      const startDate = getTimestamp();
      const questKey = getQuestKeyFromQuestId(payload.questId);
      const workshop = payload.questId === 0 ?
        state.workshop :
        {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            startDate,
          },
        };
      return {
        ...state,
        workshopStep: 0,
        gameFailures: {},
        currentQuestId: payload.questId,
        lastTimeQuestWasSwitched: startDate,
        workshop,
      };
    }

    case 'SET_LAST_WORKSHOP_QUEST_PLAYED':
      return {
        ...state,
        lastQuestFinishedBeforeEndOfQuests: state.currentQuestId || state.lastQuestFinishedBeforeEndOfQuests,
      };

    case 'SET_QUESTS_END_EXPERIENCE':
      return {
        ...state,
        shouldStartNewChallengeQuest: true,
      };

    case 'MARK_ALL_QUESTS_AND_CHALLENGES_COMPLETE':
      return {
        ...state,
        areAllQuestsAndChallengesComplete: {
          ...state.areAllQuestsAndChallengesComplete,
          [payload.product]: true,
        },
      };

    case 'UPDATE_MY_COLLECTION':
      return {
        ...state,
        myCollection: {
          ...state.myCollection,
          ...payload,
        },
      };

    case 'SELECT_REWARD_ITEM':
      return {
        ...state,
        myCollection: {
          ...state.myCollection,
          [`${payload.rewardType}`]: payload.itemName,
        },
      };

    case 'SET_VOCAB_RESULTS':
      return {
        ...state,
        vocabResults: action.result,
      };

    case 'SET_QUEST_SESSION_ID': {
      if (!state.currentQuestId || !payload.newQuestSessionId) {
        return state;
      }
      const questKey = getQuestKeyFromQuestId(state.currentQuestId);
      return {
        ...state,
        questSessionId: payload.newQuestSessionId,
        workshop: {
          ...state.workshop,
          [questKey]: {
            ...state.workshop[questKey],
            questSessionId: payload.newQuestSessionId,
          },
        },
      };
    }

    case 'SET_WORKSHOP_STEP_ID': {
      return {
        ...state,
        workshopStep: payload.step,
        isStepInProgress: false,
      };
    }

    case 'SET_WORKSHOP_STEP_AS_IN_PROGRESS': {
      return {
        ...state,
        isStepInProgress: true,
      };
    }

    case 'CLEAR_QUEST_SESSION_ID':
      return {
        ...state,
        questSessionId: '',
      };

    case 'INCREMENT_GAME_FAILURE_COUNT': {
      const { gameFailureKey, level } = payload;
      const failureCount = state.gameFailures[gameFailureKey] || 0;
      return {
        ...state,
        gameFailures: {
          ...state.gameFailures,
          [gameFailureKey]: failureCount + 1,
          currentLevel: level,
        },
      };
    }

    case 'MARK_REPLACEMENT_MODAL_AS_SEEN': {
      return {
        ...state,
        hasSeenReplacementModal: true,
        shouldShowReplacementModal: false,
      };
    }

    case 'SET_HAS_SEEN_WORKSHOP_OUTRO':
      return {
        ...state,
        hasSeenWorkshopOutro: true,
      };

    default:
      return state;
  }
};

export default workshopReducer;
